import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { ModalService } from '@src/shared/modal/modal.service';
import { ExtraProductFormatField, ProductFormatService } from '../../../../woo_services.module/ProductFormatService';
import { ExtraPublisherField, PublisherService } from '../../../../woo_services.module/PublisherService';
import { TranslationService } from '../../../../woo_services.module/TranslationService';
import { ProductFormat, Publisher, wooId } from '../../../../woo_services.module/shared-types';
import { ProductsPageUtilityService } from '../../shared/products-page-utility-service';

@Component({
  selector: 'manage-pause-ad-estimates',
  templateUrl: './manage-pause-ad-estimates.component.html',
  styleUrls: ['../../shared/table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ManagePauseAdEstimates implements OnInit {
  @Input('headerPos') parentHeader: HTMLElement;
  show = {
    estimatesHaveChanged: false,
    updatingEstimates: false,
    loading: true,
    isEditing: false,
  };

  readonly tableHeaders = ['Publicist', 'Placering', 'Andel av estimat RBS preroll', 'Maxuttag'];
  readonly staticFields = 2;
  publishers: Publisher[] = [];
  selectedPublisher: Publisher;
  productFormats: ProductFormat[];
  selectedProductFormat: ProductFormat;
  pauseAdRows: PauseAdRow[];
  translateProductFormat: (args: any) => string;

  constructor(
    private publisherService: PublisherService,
    private productFormatService: ProductFormatService,
    private modalService: ModalService,
    private utilityService: ProductsPageUtilityService,
    private translationService: TranslationService,
    private changeDetection: ChangeDetectorRef,
  ) {
    this.translateProductFormat = this.translationService.convertProductFormat;
  }

  ngOnInit(): void {
    this.publisherService
      .getPublishers(ExtraPublisherField.enabled, ExtraPublisherField.productFormats)
      .then((publishers: Publisher[]) => {
        this.publishers = publishers.filter((p) => p.enabled);
      })
      .then(() => {
        return this.getAndBuildPauseAds();
      })
      .finally(() => {
        this.show.loading = false;
        this.changeDetection.markForCheck();
      });
  }

  async getAndBuildPauseAds(): Promise<void> {
    const pfs = await this.productFormatService.getProductFormats(
      ExtraProductFormatField.enabled,
      ExtraProductFormatField.pauseAdShare,
      ExtraProductFormatField.pauseAdWithdrawalLimit,
    );

    this.productFormats = pfs.filter((f) => f.enabled);
    this.buildPauseAdRows();
  }

  setEditing(value: boolean): void {
    this.show.isEditing = value;
  }

  cancelEditing(): void {
    this.getAndBuildPauseAds();
    this.setEditing(false);
  }

  async save(): Promise<void> {
    const filterActive = this.selectedPublisher || this.selectedProductFormat;
    const textBlocks = ['Är du säker på att du vill spara dina ändringar?'];
    if (filterActive) {
      textBlocks.push('Notera att även ändringar i filtrerade rader sparas.');
    }
    this.modalService
      .openConfirm({
        title: 'Bekräfta ändringar',
        texts: textBlocks,
        confirmLabel: 'Spara',
        cancelLabel: 'Avbryt',
      })
      .then(async (status) => {
        if (status.confirm) {
          const loaderModal = this.modalService.openLoader('Sparar ändringar');
          this.show.updatingEstimates = true;
          await this.productFormatService
            .updateProductFormats(this.productFormats)
            .then((productFormats) => {
              this.productFormats = productFormats;
              this.show.estimatesHaveChanged = true;
              return this.buildPauseAdRows();
            })
            .finally(() => {
              loaderModal.close();
            });
        } else {
          this.getAndBuildPauseAds();
        }
      })
      .finally(() => {
        this.setEditing(false);
        this.show.updatingEstimates = false;
        this.changeDetection.markForCheck();
      });
  }

  rowVisible(obj: PauseAdRow): boolean {
    const publisherFiltered = this.selectedPublisher && obj.publisherId !== this.selectedPublisher.id;
    const productFormatFiltered = this.selectedProductFormat && obj.productFormatId !== this.selectedProductFormat.id;
    return !(publisherFiltered || productFormatFiltered);
  }

  getProductFormat(productFormatId: wooId): ProductFormat {
    return this.productFormats.find((format) => format.id === productFormatId);
  }

  private buildPauseAdRows() {
    let pauseAdRows: PauseAdRow[] = [];

    this.publishers.map((publisher) => {
      this.utilityService.getPublisherSpecificProductFormats(publisher, this.productFormats).map((product_format) => {
        pauseAdRows = [
          ...pauseAdRows,
          {
            publisher: publisher.name,
            publisherId: publisher.id,
            productFormat: product_format.name,
            productFormatId: product_format.id,
            pauseAdShare: product_format.pause_ad_share,
            pausAdWithdrawalLimit: product_format.pause_ad_withdrawal_limit,
          },
        ];
      });
    });
    this.pauseAdRows = pauseAdRows;
    return this.pauseAdRows;
  }
}
interface PauseAdRow {
  publisher: string;
  publisherId?: wooId;
  productFormat: string;
  productFormatId: wooId;
  pauseAdShare?: number;
  pausAdWithdrawalLimit?: number;
}
